import type { ActiveSessionMode } from "@/lib/sessionRouting";

export type SessionLaunchLanguageLevel =
  | "absolute_beginner"
  | "beginner"
  | "intermediate"
  | "advanced";

export type PreSessionVariant = "default" | "start-only" | "configurable-start";

export type SessionPrewarmPolicy = "normal" | "fresh-on-start";

export type SessionLaunchSurface = "standard" | "acquisition-demo";

export type SessionPreferenceStorage = "persistent" | "session";

export type SessionAnalyticsSurface =
  | "fullscreen"
  | "landing"
  | "onboarding"
  | "embed"
  | "maths"
  | "other";

export type SessionLaunchPreset = {
  id: string;
  targetLanguageCode?: string;
  instructionLanguageCode?: string;
  languageLevel: SessionLaunchLanguageLevel;
  runtimeSessionMode: ActiveSessionMode;
  preSessionVariant: PreSessionVariant;
  analyticsSurface: SessionAnalyticsSurface;
  analyticsEventPrefix: string;
  enableFreemiumPreSessionOffer: boolean;
  prewarmPolicy?: SessionPrewarmPolicy;
  launchSurface?: SessionLaunchSurface;
  preferenceStorage?: SessionPreferenceStorage;
  customInstructions?: string;
};

export const ACQUISITION_SPEAKING_DEMO_PRESET: SessionLaunchPreset = {
  id: "acquisition-speaking-demo",
  languageLevel: "beginner",
  runtimeSessionMode: "conversation",
  preSessionVariant: "configurable-start",
  analyticsSurface: "embed",
  analyticsEventPrefix: "acquisition_demo",
  enableFreemiumPreSessionOffer: false,
  prewarmPolicy: "fresh-on-start",
  launchSurface: "acquisition-demo",
  preferenceStorage: "session",
};

export const AIFRENCHTUTOR_BEGINNER_DEMO_PRESET: SessionLaunchPreset = {
  id: "aifrenchtutor-beginner-demo",
  targetLanguageCode: "fr-FR",
  instructionLanguageCode: "en-US",
  languageLevel: "beginner",
  runtimeSessionMode: "conversation",
  preSessionVariant: "start-only",
  analyticsSurface: "embed",
  analyticsEventPrefix: "aifrenchtutor_embed",
  enableFreemiumPreSessionOffer: false,
  prewarmPolicy: "fresh-on-start",
  preferenceStorage: "session",
};

export const AIDUTCHTUTOR_BEGINNER_DEMO_PRESET: SessionLaunchPreset = {
  id: "aidutchtutor-beginner-demo",
  targetLanguageCode: "nl-NL",
  instructionLanguageCode: "fr-FR",
  languageLevel: "beginner",
  runtimeSessionMode: "conversation",
  preSessionVariant: "start-only",
  analyticsSurface: "embed",
  analyticsEventPrefix: "aidutchtutor_embed",
  enableFreemiumPreSessionOffer: false,
  prewarmPolicy: "fresh-on-start",
  preferenceStorage: "session",
};

export const AIDUTCHTUTOR_ENGLISH_BEGINNER_DEMO_PRESET: SessionLaunchPreset = {
  ...AIDUTCHTUTOR_BEGINNER_DEMO_PRESET,
  id: "aidutchtutor-english-beginner-demo",
  instructionLanguageCode: "en-US",
  analyticsEventPrefix: "aidutchtutor_english_embed",
};

export const AIFRENCHTUTOR_LESSON_MATERIAL_DEMO_PRESET: SessionLaunchPreset = {
  id: "aifrenchtutor-lesson-material-demo",
  targetLanguageCode: "fr-FR",
  instructionLanguageCode: "en-US",
  languageLevel: "beginner",
  runtimeSessionMode: "conversation",
  preSessionVariant: "start-only",
  analyticsSurface: "embed",
  analyticsEventPrefix: "aifrenchtutor_lesson_material_embed",
  enableFreemiumPreSessionOffer: false,
  prewarmPolicy: "fresh-on-start",
  preferenceStorage: "session",
  customInstructions:
    "This is a fixed sample of between-lesson French speaking practice. The learner's last lesson covered ordering at a cafe: greetings, je voudrais, s'il vous plait, drinks, pastries, and prices. Run a short cafe role-play in French. Ask one question at a time. Correct only the most useful blocker, ask the learner to repeat the improved sentence, then continue the cafe exchange. Do not claim to have read or received an uploaded file.",
};

export const AIDUTCHTUTOR_LESSON_MATERIAL_DEMO_PRESET: SessionLaunchPreset = {
  id: "aidutchtutor-lesson-material-demo",
  targetLanguageCode: "nl-NL",
  instructionLanguageCode: "en-US",
  languageLevel: "beginner",
  runtimeSessionMode: "conversation",
  preSessionVariant: "start-only",
  analyticsSurface: "embed",
  analyticsEventPrefix: "aidutchtutor_lesson_material_embed",
  enableFreemiumPreSessionOffer: false,
  prewarmPolicy: "fresh-on-start",
  preferenceStorage: "session",
  customInstructions:
    "This is a fixed sample of between-lesson Dutch speaking practice. The learner's last lesson covered making an appointment: days, times, kunnen, willen, and polite confirmation. Run a short appointment-booking role-play in Dutch. Ask one question at a time. Correct only the most useful blocker, ask the learner to repeat the improved sentence, then continue the appointment exchange. Do not claim to have read or received an uploaded file.",
};

export const AISWEDISHTUTOR_LESSON_MATERIAL_DEMO_PRESET: SessionLaunchPreset = {
  id: "aiswedishtutor-lesson-material-demo",
  targetLanguageCode: "sv-SE",
  instructionLanguageCode: "en-US",
  languageLevel: "beginner",
  runtimeSessionMode: "conversation",
  preSessionVariant: "start-only",
  analyticsSurface: "embed",
  analyticsEventPrefix: "aiswedishtutor_lesson_material_embed",
  enableFreemiumPreSessionOffer: false,
  prewarmPolicy: "fresh-on-start",
  preferenceStorage: "session",
  customInstructions:
    "This is a fixed sample of between-lesson Swedish speaking practice. The learner's last lesson covered fika introductions: greetings, saying what you would like, asking a simple follow-up, and talking briefly about the day. Run a short fika role-play in Swedish. Ask one question at a time. Correct only the most useful blocker, especially sentence melody or a definite-form pattern, ask the learner to repeat the improved sentence, then continue the exchange. Do not claim to have read or received an uploaded file.",
};

export const AISPANISHCOACH_LESSON_MATERIAL_DEMO_PRESET: SessionLaunchPreset = {
  id: "aispanishcoach-lesson-material-demo",
  targetLanguageCode: "es-ES",
  instructionLanguageCode: "en-US",
  languageLevel: "beginner",
  runtimeSessionMode: "conversation",
  preSessionVariant: "start-only",
  analyticsSurface: "embed",
  analyticsEventPrefix: "aispanishcoach_lesson_material_embed",
  enableFreemiumPreSessionOffer: false,
  prewarmPolicy: "fresh-on-start",
  preferenceStorage: "session",
  customInstructions:
    "This is a fixed sample of between-lesson Spanish speaking practice. The learner's last lesson covered ordering in a restaurant: greetings, quisiera, food and drink choices, asking for the price, and polite confirmation. Run a short restaurant role-play in Spanish from Spain. Ask one question at a time. Correct only the most useful blocker, especially an article, preposition, or natural service phrase, ask the learner to repeat the improved sentence, then continue the exchange. Do not claim to have read or received an uploaded file.",
};
